import { toJpeg } from 'html-to-image';

/**
 * Poster rendering helpers.
 *
 * We use `html-to-image` (SVG <foreignObject>) rather than html2canvas because
 * foreignObject is rendered by the browser's NATIVE text engine, so complex
 * Bengali script (conjuncts + vowel signs, e.g. জুলাই) shapes correctly.
 *
 * The poster background/image comes from R2 (files.sohozkaj.com). R2 serves
 * proper CORS (Vary: Origin), but if the browser already cached a NON-CORS copy
 * (from a plain <img> display), a later CORS request reuses that bad cache and
 * the image comes out blank. We therefore convert every remote image to a
 * data-URL up front, using a crossorigin <img> + canvas with a cache-busting
 * query param to force a fresh CORS fetch. Once inlined, html-to-image needs no
 * network access at all.
 */

// Export the poster at ≥ A4 short-edge @300dpi (2480px wide) so downloads/prints
// stay crisp at A4 or larger. The on-screen poster is only a few hundred px wide,
// so the pixelRatio is derived per-node to reach the target width.
const TARGET_MIN_WIDTH = 2480;

const BASE_OPTS = {
    quality: 0.95,
    backgroundColor: '#0d3b2b',
    cacheBust: false, // not needed — images are pre-inlined as data URLs
};

function captureOpts(node) {
    const width = node.offsetWidth || 800;
    return { ...BASE_OPTS, pixelRatio: Math.max(2, Math.ceil(TARGET_MIN_WIDTH / width)) };
}

/**
 * Load a (possibly cross-origin) image URL and return it as a data URL.
 * Encoded as PNG to preserve transparency — logos/emblems are transparent
 * PNGs, and JPEG (no alpha) would flatten their transparent areas to black.
 */
export function imageUrlToDataUrl(url) {
    return new Promise((resolve, reject) => {
        if (!url || url.startsWith('data:')) return resolve(url);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d').drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            } catch (e) {
                reject(e);
            }
        };
        img.onerror = () => reject(new Error('image load failed'));
        // Cache-bust so the browser makes a fresh CORS request (bypasses any
        // previously cached non-CORS copy of the same URL).
        const sep = url.includes('?') ? '&' : '?';
        img.src = `${url}${sep}cb=${Date.now()}`;
    });
}

/** Swap every <img> inside a node to an inlined data URL; returns a restore fn. */
async function inlineNodeImages(node) {
    const imgs = [...node.querySelectorAll('img')];
    const restores = [];
    await Promise.all(
        imgs.map(async (img) => {
            const src = img.getAttribute('src');
            if (!src || src.startsWith('data:')) return;
            try {
                const dataUrl = await imageUrlToDataUrl(src);
                restores.push([img, src]);
                img.setAttribute('src', dataUrl);
                await img.decode().catch(() => {});
            } catch {
                /* leave original src on failure */
            }
        })
    );
    return () => restores.forEach(([img, src]) => img.setAttribute('src', src));
}

/** Capture a DOM node (poster image + shop footer) to a high-res JPEG data URL. */
export async function posterDataUrl(node) {
    if (!node) return null;
    const restore = await inlineNodeImages(node);
    try {
        return await toJpeg(node, captureOpts(node));
    } finally {
        restore();
    }
}

/** Capture a DOM node (poster image + shop footer) and trigger a JPG download. */
export async function downloadPoster(node, filename = 'biggapon.jpg') {
    const dataUrl = await posterDataUrl(node);
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
}

/** Flatten a DOM node into a JPEG Blob (used to bake the poster in the admin panel). */
export async function nodeToJpegBlob(node) {
    const dataUrl = await posterDataUrl(node);
    if (!dataUrl) return null;
    const res = await fetch(dataUrl); // data: URL — no network
    return res.blob();
}
